<template>
  <!-- ══ Strip — compartilha o trilho de 60px com o ConvDrawer; só um dos dois
       fica visível por vez (useChatRailMode) ══ -->
  <div v-if="railMode === 'agent' && canUseAgentChat" class="ach-strip" :class="{ 'is-expanded': panelOpen }">
    <div class="ach-strip-cap"></div>

    <div class="ach-strip-head" @click="togglePanel" :title="panelOpen ? 'Fechar' : 'Chat entre agentes'">
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
      </svg>
      <span v-if="totalUnread > 0" class="ach-strip-badge">{{ totalUnread > 9 ? '9+' : totalUnread }}</span>
    </div>

    <div class="ach-av-list-wrap">
      <button v-if="canScrollUp" class="ach-scroll-btn up" @click="scrollList(-1)" title="Rolar para cima">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="18 15 12 9 6 15"/>
        </svg>
      </button>

      <div class="ach-av-list" ref="avListEl" @scroll="updateScrollState">
        <div
          v-for="a in agents"
          :key="a.id"
          class="ach-av-item"
          :class="{ active: activeAgentId === a.id }"
          @click="openAgent(a.id)"
          @mouseenter="showAgentTT(a, $event)"
          @mouseleave="hideAgentTT"
        >
          <div class="ach-av" :style="{ background: avatarColor(a.name) }">
            <img
              v-if="a.avatarUrl"
              :src="a.avatarUrl"
              :alt="a.name"
              @error="(e) => (e.target as HTMLImageElement).style.display = 'none'"
            />
            <span v-else>{{ initials(a.name) }}</span>
          </div>
          <span class="ach-status-dot" :style="{ background: statusColor(a.availability) }"></span>
          <span v-if="unreadByAgent[a.id]" class="ach-av-unread">{{ unreadByAgent[a.id] > 9 ? '9+' : unreadByAgent[a.id] }}</span>
        </div>

        <div v-if="!agents.length && !loadingAgents" class="ach-strip-empty">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
      </div>

      <button v-if="canScrollDown" class="ach-scroll-btn down" @click="scrollList(1)" title="Rolar para baixo">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
    </div>

    <!-- Card com dados do agente ao passar o mouse — mesmo gatilho/posicionamento do
         "cdr-tt-pill" usado nos avatares de contatos de cliente (ConvDrawer.vue), mas
         em formato de card (retângulo com bordas arredondadas) com mais informações. -->
    <Teleport to="body">
      <transition name="tt-pill">
        <div
          v-if="ttAgent"
          class="ach-tt-card"
          :style="{ top: ttAgent.y + 'px', right: (STRIP_W + 8) + 'px' }"
        >
          <div class="ach-ttc-head">
            <div class="ach-ttc-av" :style="{ background: avatarColor(ttAgent.agent.name) }">
              <img
                v-if="ttAgent.agent.avatarUrl"
                :src="ttAgent.agent.avatarUrl"
                :alt="ttAgent.agent.name"
                @error="(e) => (e.target as HTMLImageElement).style.display='none'"
              />
              <span v-else>{{ initials(ttAgent.agent.name) }}</span>
              <span class="ach-ttc-av-dot" :style="{ background: statusColor(ttAgent.agent.availability) }"></span>
            </div>
            <div class="ach-ttc-headinfo">
              <div class="ach-ttc-row1">
                <span class="ach-ttc-name">{{ ttAgent.agent.name }}</span>
                <span v-if="unreadByAgent[ttAgent.agent.id]" class="ach-ttc-unread">
                  {{ unreadByAgent[ttAgent.agent.id] > 9 ? '9+' : unreadByAgent[ttAgent.agent.id] }}
                </span>
              </div>
              <span v-if="ttAgent.agent.email" class="ach-ttc-email">{{ ttAgent.agent.email }}</span>
            </div>
          </div>

          <div class="ach-ttc-divider"></div>

          <div class="ach-ttc-body">
            <span class="ach-ttc-status">
              <span class="ach-ttc-dot" :style="{ background: statusColor(ttAgent.agent.availability) }"></span>
              {{ statusLabel(ttAgent.agent.availability) }}
            </span>
            <span v-if="openCountFor(ttAgent.agent.id) > 0" class="ach-ttc-atend">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
                <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
              </svg>
              Em atendimento · {{ openCountFor(ttAgent.agent.id) }}
            </span>
            <span v-else class="ach-ttc-free">Livre no momento</span>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- Rodapé — alterna para o chat de clientes (ícone = chat entre agentes, o modo atual) -->
    <div class="ach-strip-footer" @click="toggleRailMode" title="Alternar para chat de clientes">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
        <path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
      <span v-if="totalUnread > 0" class="ach-strip-footer-badge">{{ totalUnread > 9 ? '9+' : totalUnread }}</span>
    </div>
  </div>

  <!-- ══ Painel (lista / chat) ══ -->
  <transition name="ach-panel">
    <div v-if="panelOpen && railMode === 'agent' && canUseAgentChat" class="ach-panel" :style="{ width: panelWidth + 'px' }">
      <div class="ach-resize-handle" @mousedown.prevent="startResize"></div>

      <!-- ── Lista de agentes ── -->
      <template v-if="activeAgentId == null">
        <div class="ach-head">
          <div class="ach-head-title">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
            </svg>
            Chat entre agentes
          </div>
          <button class="ach-icon-btn" @click="closePanel">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div v-if="agents.length" class="ach-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            v-model="agentSearch"
            type="text"
            class="ach-search-input"
            placeholder="Buscar agente…"
          />
          <button v-if="agentSearch" class="ach-search-clear" @click="agentSearch = ''" title="Limpar busca">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div v-if="loadingAgents" class="ach-empty">Carregando agentes…</div>
        <div v-else-if="!agents.length" class="ach-empty">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
          </svg>
          <p>Nenhum outro agente encontrado</p>
        </div>
        <div v-else-if="!filteredAgents.length" class="ach-empty">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <p>Nenhum agente encontrado para "{{ agentSearch }}"</p>
        </div>
        <div v-else class="ach-list">
          <div v-for="a in filteredAgents" :key="a.id" class="ach-item" @click="openAgent(a.id)">
            <div class="ach-item-av-wrap">
              <div class="ach-item-av" :style="{ background: avatarColor(a.name) }">
                <img
                  v-if="a.avatarUrl"
                  :src="a.avatarUrl"
                  :alt="a.name"
                  @error="(e) => (e.target as HTMLImageElement).style.display = 'none'"
                />
                <span v-else>{{ initials(a.name) }}</span>
              </div>
              <span class="ach-status-dot sm" :style="{ background: statusColor(a.availability) }"></span>
            </div>
            <div class="ach-item-body">
              <span class="ach-item-name">{{ a.name }}</span>
              <span class="ach-item-status">{{ statusLabel(a.availability) }}</span>
            </div>
            <span v-if="unreadByAgent[a.id]" class="ach-item-unread">{{ unreadByAgent[a.id] > 9 ? '9+' : unreadByAgent[a.id] }}</span>
          </div>
        </div>
      </template>

      <!-- ── Chat ── -->
      <template v-else>
        <div class="ach-head">
          <button class="ach-icon-btn" @click="backToList" title="Voltar">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <div class="ach-chat-hinfo" v-if="activeAgent">
            <div class="ach-chat-hav" :style="{ background: avatarColor(activeAgent.name) }">
              <img
                v-if="activeAgent.avatarUrl"
                :src="activeAgent.avatarUrl"
                :alt="activeAgent.name"
                @error="(e) => (e.target as HTMLImageElement).style.display = 'none'"
              />
              <span v-else>{{ initials(activeAgent.name) }}</span>
            </div>
            <div class="ach-chat-hname-wrap">
              <div class="ach-chat-hname">{{ activeAgent.name }}</div>
              <div class="ach-chat-hstatus">
                <span class="ach-status-dot sm" :style="{ background: statusColor(activeAgent.availability) }"></span>
                {{ statusLabel(activeAgent.availability) }}
              </div>
            </div>
          </div>
          <button class="ach-icon-btn" @click="closePanel">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div class="ach-messages" ref="msgListEl">
          <div v-if="loadingMessages" class="ach-empty">Carregando…</div>
          <template v-else>
            <div v-if="!activeMessages.length" class="ach-empty">
              <p>Nenhuma mensagem ainda — diga olá!</p>
            </div>
            <div
              v-for="m in activeMessages"
              :key="m.id"
              class="ach-msg-row"
              :class="m.senderId === myId ? 'out' : 'inn'"
            >
              <div class="ach-bubble">
                <span class="ach-bubble-text">{{ m.body }}</span>
                <span class="ach-bubble-ts">{{ fmtTs(m.createdAt) }}</span>
              </div>
            </div>
          </template>
        </div>

        <div v-if="sendError" class="ach-error">{{ sendError }}</div>

        <form class="ach-input-row" @submit.prevent="onSend">
          <input
            v-model="draft"
            class="ach-input"
            placeholder="Mensagem… (Enter envia)"
            :disabled="sending"
          />
          <button type="submit" class="ach-send-btn" :disabled="!draft.trim() || sending">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </form>
      </template>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { useAgentChat } from '@/composables/useAgentChat'
import { useChatRailMode } from '@/composables/useChatRailMode'
import { usePermission } from '@/composables/usePermission'
import { avatarColor, initials } from '@/composables/useHelpers'
import { AVAIL_OPTIONS } from '@/composables/useAvailability'
import { useConversationStore } from '@/composables/useConversationStore'
import type { ChatAgent } from '@/types/agentChat'

const store    = useStore()
const authUser = computed(() => store.getters.authUser)
const myId     = computed(() => authUser.value?.id ?? 0)

// Mesma fonte que o Dashboard/AgentsTab usam pra "em atendimento" — estado de
// módulo já mantido por useDashboardEngine.loadData(), sem fetch próprio aqui.
const { conversations } = useConversationStore()
function openCountFor(agentId: number): number {
  return conversations.value.filter(c => c.assignee?.id === agentId && c.status === 'open').length
}

const {
  agents, unreadByAgent, totalUnread, activeAgentId, activeAgent, activeMessages,
  panelOpen, loadingAgents, loadingMessages, sendError,
  loadAgents, loadUnread, openThread, backToList, togglePanel, closePanel, send,
  startPolling, stopPolling,
} = useAgentChat()

const { mode: railMode, toggle: toggleRailMode } = useChatRailMode()
const { can } = usePermission()
const canUseAgentChat = computed(() => can('agentChat.view'))

const draft      = ref('')
const sending    = ref(false)
const msgListEl  = ref<HTMLElement | null>(null)

// Busca/filtro por nome ou e-mail na lista de agentes do painel
const agentSearch = ref('')
const filteredAgents = computed(() => {
  const q = agentSearch.value.trim().toLowerCase()
  if (!q) return agents.value
  return agents.value.filter(a => a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q))
})
watch(panelOpen, open => { if (!open) agentSearch.value = '' })

// Largura fixa do trilho (.ach-strip) — usado pra posicionar o card de hover
// (ach-tt-card) encostado nele, mesmo cálculo do STRIP_W em ConvDrawer.vue.
const STRIP_W = 60

/* ── Scroll da lista de avatares (setas) ──────────────────────────
   A lista já era scrollável (overflow-y: auto), só não tinha nenhum indício
   visual disso — com mais agentes do que cabem no trilho, dava pra achar que
   só os visíveis existiam. */
const avListEl     = ref<HTMLElement | null>(null)
const canScrollUp   = ref(false)
const canScrollDown = ref(false)

function updateScrollState() {
  const el = avListEl.value
  if (!el) { canScrollUp.value = false; canScrollDown.value = false; return }
  canScrollUp.value   = el.scrollTop > 4
  canScrollDown.value = el.scrollTop + el.clientHeight < el.scrollHeight - 4
}

function scrollList(dir: 1 | -1) {
  avListEl.value?.scrollBy({ top: dir * 120, behavior: 'smooth' })
}

watch(agents, () => nextTick(updateScrollState), { flush: 'post' })

// Reavalia quando o container muda de altura (resize da janela, DevTools
// abrindo/fechando, zoom) — sem isso, canScrollUp/Down ficava travado no
// valor calculado só na última vez que `agents` mudou ou a lista rolou.
let avListResizeObserver: ResizeObserver | null = null
watch(avListEl, el => {
  avListResizeObserver?.disconnect()
  avListResizeObserver = null
  if (!el) return
  avListResizeObserver = new ResizeObserver(() => updateScrollState())
  avListResizeObserver.observe(el)
  nextTick(updateScrollState)
})

onMounted(() => nextTick(updateScrollState))
onUnmounted(() => avListResizeObserver?.disconnect())

/* ── Card com dados do agente ao passar o mouse (mesmo padrão do
   cdr-tt-pill/showTT de ConvDrawer.vue, aplicado aqui aos agentes) ── */
const ttAgent = ref<{ agent: ChatAgent; y: number } | null>(null)

function showAgentTT(agent: ChatAgent, e: MouseEvent) {
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
  ttAgent.value = { agent, y: Math.round(rect.top + rect.height / 2) }
}
function hideAgentTT() { ttAgent.value = null }

function statusColor(av: string): string {
  return AVAIL_OPTIONS.find(o => o.value === av)?.color ?? '#9ca3af'
}
function statusLabel(av: string): string {
  return AVAIL_OPTIONS.find(o => o.value === av)?.label ?? 'Offline'
}

function fmtTs(ts: number): string {
  const d = new Date(ts * 1000)
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
}

function scrollToBottom() {
  nextTick(() => {
    if (msgListEl.value) msgListEl.value.scrollTop = msgListEl.value.scrollHeight
  })
}

/* ── Resize — mesmo mecanismo do ConvDrawer (cdr_panel_w), chave própria ── */
const MIN_W = 260
const MAX_W = 640
const panelWidth = ref(Math.max(MIN_W, Math.min(MAX_W, parseInt(localStorage.getItem('ach_panel_w') || '320'))))

let _rx = false, _sx = 0, _sw = 0

function startResize(e: MouseEvent) {
  _rx = true; _sx = e.clientX; _sw = panelWidth.value
  document.addEventListener('mousemove', doResize)
  document.addEventListener('mouseup', stopResize)
  document.body.style.userSelect = 'none'
  document.body.style.cursor = 'col-resize'
}
function doResize(e: MouseEvent) {
  if (!_rx) return
  panelWidth.value = Math.max(MIN_W, Math.min(MAX_W, _sw + (_sx - e.clientX)))
}
function stopResize() {
  _rx = false
  document.removeEventListener('mousemove', doResize)
  document.removeEventListener('mouseup', stopResize)
  document.body.style.userSelect = ''
  document.body.style.cursor = ''
  localStorage.setItem('ach_panel_w', String(panelWidth.value))
}

async function openAgent(id: number) {
  panelOpen.value = true
  await openThread(myId.value, id)
  scrollToBottom()
}

async function onSend() {
  const text = draft.value
  if (!text.trim() || activeAgentId.value == null) return
  draft.value = ''
  sending.value = true
  await send(myId.value, activeAgentId.value, text)
  sending.value = false
  scrollToBottom()
}

watch(activeMessages, scrollToBottom)

// Carrega agentes/não-lidas e arma o polling assim que o token E a permissão
// (agentChat.view) estiverem disponíveis — cobre o mount inicial e também o
// caso do useAdmin ainda estar carregando os perfis nesse instante (a
// permissão libera um instante depois do token, não junto).
watch(
  () => [authUser.value?.access_token, canUseAgentChat.value] as const,
  ([token, allowed]) => {
    if (!token || !allowed) {
      if (!allowed) stopPolling()
      return
    }
    loadAgents(token, myId.value)
    loadUnread(myId.value)
    startPolling(token, myId.value)
  },
  { immediate: true },
)
</script>

<style scoped>
.ach-strip {
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 60px;
  background: #021627;
  border-left: 1px solid rgba(255,255,255,.06);
  box-shadow: -3px 0 18px rgba(2,22,39,.45);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 189;
  overflow: visible;
}

.ach-strip-cap {
  width: 100%;
  height: 66px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255,255,255,.07);
}

.ach-strip-head {
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  color: rgba(255,255,255,.45);
  border-bottom: 1px solid rgba(255,255,255,.07);
  flex-shrink: 0;
  transition: background 0.14s, color 0.14s;
}
.ach-strip-head:hover { background: rgba(255,255,255,.07); color: rgba(255,255,255,.9); }
.is-expanded .ach-strip-head { color: #fff; }

.ach-strip-badge {
  position: absolute;
  top: 5px; right: 9px;
  background: var(--red);
  color: #fff;
  font-size: 9.5px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 20px;
  line-height: 1.4;
}

.ach-av-list-wrap {
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.ach-av-list {
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 12px 0;
}
.ach-av-list::-webkit-scrollbar { width: 0; }

/* Setas de rolagem — só aparecem quando há mais agentes do que cabem. Fundo
   claro (não escuro-sobre-escuro como antes, quase invisível no trilho) +
   sombra pra destacar sobre os avatares. */
.ach-scroll-btn {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 30px; height: 20px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,.9);
  border: none;
  border-radius: 999px;
  color: #021627;
  cursor: pointer;
  z-index: 3;
  box-shadow: 0 2px 8px rgba(0,0,0,.45);
  transition: background 0.14s, transform 0.14s;
}
.ach-scroll-btn:hover { background: var(--red); color: #fff; transform: translateX(-50%) scale(1.1); }
.ach-scroll-btn.up   { top: 4px; }
.ach-scroll-btn.down { bottom: 4px; }

/* Rodapé — alterna entre chat de clientes e chat entre agentes (useChatRailMode) */
.ach-strip-footer {
  position: relative;
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255,255,255,.45);
  border-top: 1px solid rgba(255,255,255,.07);
  flex-shrink: 0;
  transition: background 0.14s, color 0.14s;
}
.ach-strip-footer:hover { background: rgba(255,255,255,.07); color: rgba(255,255,255,.9); }
.ach-strip-footer-badge {
  position: absolute;
  top: 5px; right: 9px;
  background: var(--red);
  color: #fff;
  font-size: 9.5px;
  font-weight: 800;
  min-width: 15px; height: 15px;
  border-radius: 999px;
  display: flex; align-items: center; justify-content: center;
  padding: 0 3px;
  line-height: 1;
}

.ach-av-item {
  position: relative;
  cursor: pointer;
  flex-shrink: 0;
}
.ach-av {
  width: 34px; height: 34px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 12px; font-weight: 700;
  overflow: hidden;
  border: 2px solid transparent;
  transition: border-color 0.14s;
}
.ach-av-item.active .ach-av { border-color: #fff; }
.ach-av img { width: 100%; height: 100%; object-fit: cover; }

.ach-status-dot {
  position: absolute; bottom: -1px; right: -1px;
  width: 9px; height: 9px; border-radius: 50%;
  border: 2px solid #021627;
}
.ach-status-dot.sm { position: static; border: 2px solid #fff; width: 9px; height: 9px; flex-shrink: 0; }

.ach-av-unread {
  position: absolute; top: -4px; left: -4px;
  background: var(--red);
  color: #fff;
  font-size: 9px; font-weight: 700;
  min-width: 15px; text-align: center;
  padding: 1px 3px;
  border-radius: 20px;
  line-height: 1.4;
  border: 2px solid #021627;
}

.ach-strip-empty {
  color: rgba(255,255,255,.25);
  padding-top: 8px;
}

/* ── Painel ── */
.ach-panel {
  position: fixed;
  top: 66px; bottom: 0;
  right: 60px;
  background: #fff;
  box-shadow: -6px 0 24px rgba(0,0,0,.18);
  z-index: 188;
  display: flex;
  flex-direction: column;
}

/* Handle de resize */
.ach-resize-handle {
  position: absolute; left: 0; top: 0; bottom: 0; width: 5px;
  cursor: col-resize; z-index: 10;
  transition: background 0.15s;
}
.ach-resize-handle:hover { background: rgba(188,12,36,.18); }
.ach-panel-enter-active, .ach-panel-leave-active { transition: transform .2s ease, opacity .2s ease; }
.ach-panel-enter-from, .ach-panel-leave-to { transform: translateX(16px); opacity: 0; }

.ach-head {
  height: 66px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
  border-bottom: 1px solid rgba(0,0,0,.08);
  background: var(--red-dark);
  color: #fff;
}
.ach-head-title {
  flex: 1;
  display: flex; align-items: center; gap: 7px;
  font-size: 13.5px; font-weight: 700;
}
.ach-icon-btn {
  width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,.1);
  border: none; border-radius: 50%;
  color: rgba(255,255,255,.85);
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.14s;
}
.ach-icon-btn:hover { background: rgba(255,255,255,.2); }

.ach-chat-hinfo { flex: 1; display: flex; align-items: center; gap: 8px; min-width: 0; }
.ach-chat-hav {
  width: 32px; height: 32px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 11px; font-weight: 700;
  overflow: hidden; flex-shrink: 0;
}
.ach-chat-hav img { width: 100%; height: 100%; object-fit: cover; }
.ach-chat-hname-wrap { min-width: 0; }
.ach-chat-hname {
  font-size: 12.5px; font-weight: 700;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ach-chat-hstatus {
  display: flex; align-items: center; gap: 5px;
  font-size: 10.5px; color: rgba(255,255,255,.7);
}

.ach-empty {
  flex: 1;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 12.5px;
  text-align: center;
  padding: 24px;
}

.ach-search {
  display: flex; align-items: center; gap: 8px;
  flex-shrink: 0;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(0,0,0,.06);
  color: #94a3b8;
}
.ach-search-input {
  flex: 1; min-width: 0;
  border: none; outline: none;
  background: transparent;
  font-size: 12.5px;
  font-family: inherit;
  color: #1a1a1a;
}
.ach-search-input::placeholder { color: #94a3b8; }
.ach-search-clear {
  display: flex; align-items: center; justify-content: center;
  width: 18px; height: 18px;
  border: none; border-radius: 50%;
  background: rgba(0,0,0,.06);
  color: #64748b;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.14s;
}
.ach-search-clear:hover { background: rgba(0,0,0,.12); }

.ach-list { flex: 1; overflow-y: auto; }
.ach-item {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px;
  cursor: pointer;
  border-bottom: 1px solid rgba(0,0,0,.05);
  transition: background 0.14s;
}
.ach-item:hover { background: rgba(0,0,0,.03); }
.ach-item-av-wrap { position: relative; flex-shrink: 0; }
.ach-item-av {
  width: 36px; height: 36px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 12.5px; font-weight: 700;
  overflow: hidden;
}
.ach-item-av img { width: 100%; height: 100%; object-fit: cover; }
.ach-item-body { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.ach-item-name { font-size: 12.5px; font-weight: 600; color: #1a1a1a; }
.ach-item-status { font-size: 10.5px; color: #94a3b8; }
.ach-item-unread {
  background: var(--red);
  color: #fff;
  font-size: 10px; font-weight: 700;
  min-width: 17px; text-align: center;
  padding: 2px 5px;
  border-radius: 20px;
  flex-shrink: 0;
}

.ach-messages {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #f4f5f7;
}

.ach-msg-row { display: flex; }
.ach-msg-row.out { justify-content: flex-end; }
.ach-msg-row.inn { justify-content: flex-start; }

.ach-bubble {
  max-width: 78%;
  padding: 7px 10px;
  border-radius: 12px;
}
.ach-msg-row.out .ach-bubble { background: var(--red); color: #fff; border-bottom-right-radius: 3px; }
.ach-msg-row.inn .ach-bubble { background: #fff; color: #1a1a1a; border-bottom-left-radius: 3px; box-shadow: 0 1px 3px rgba(0,0,0,.07); }
.ach-bubble-text { display: block; font-size: 12.5px; line-height: 1.45; white-space: pre-wrap; word-break: break-word; }
.ach-bubble-ts { display: block; text-align: right; font-size: 10px; margin-top: 2px; }
.ach-msg-row.inn .ach-bubble-ts { color: rgba(0,0,0,.45); }
.ach-msg-row.out .ach-bubble-ts { color: rgba(255,255,255,.65); }

.ach-error {
  padding: 6px 14px;
  font-size: 11.5px;
  color: #ef4444;
  background: rgba(239,68,68,.08);
}

.ach-input-row {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid rgba(0,0,0,.08);
  background: #fff;
}
.ach-input {
  flex: 1;
  border: 1px solid rgba(0,0,0,.12);
  border-radius: 20px;
  padding: 8px 14px;
  font-size: 12.5px;
  font-family: inherit;
  outline: none;
  background: #fff;
  color: #1a1a1a;
}
.ach-input:focus { border-color: var(--red); }
.ach-send-btn {
  width: 32px; height: 32px;
  border-radius: 50%;
  border: none;
  background: var(--red);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: opacity 0.14s;
}
.ach-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* ── Card de hover no avatar — retângulo com bordas arredondadas (não pílula),
   mesmo gatilho/posicionamento do cdr-tt-pill de ConvDrawer.vue mas com mais
   informação: e-mail, status e se está em atendimento agora. ── */
.ach-tt-card {
  position: fixed;
  transform: translateY(-50%);
  width: 230px;
  background: rgba(2, 22, 39, 0.97);
  border: 1px solid rgba(255,255,255,0.13);
  border-radius: 14px;
  padding: 12px 14px;
  pointer-events: none;
  z-index: 9500;
  box-shadow: 0 8px 32px rgba(0,0,0,.6);
  backdrop-filter: blur(8px);
}
.tt-pill-enter-active {
  transition: opacity 0.18s ease, transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.tt-pill-leave-active { transition: opacity 0.12s ease; }
.tt-pill-enter-from   { opacity: 0; transform: translateY(-50%) translateX(16px); }
.tt-pill-leave-to     { opacity: 0; }

.ach-ttc-head { display: flex; align-items: center; gap: 10px; }
.ach-ttc-av {
  position: relative;
  width: 38px; height: 38px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 800;
  color: #fff;
  overflow: hidden;
  border: 2px solid rgba(255,255,255,0.15);
}
.ach-ttc-av img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
.ach-ttc-av-dot {
  position: absolute; bottom: -1px; right: -1px;
  width: 9px; height: 9px; border-radius: 50%;
  border: 2px solid #021627;
}

.ach-ttc-headinfo { min-width: 0; flex: 1; }
.ach-ttc-row1 { display: flex; align-items: center; gap: 8px; }
.ach-ttc-name {
  font-size: 13px; font-weight: 700; color: #fff;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  flex: 1; min-width: 0;
}
.ach-ttc-unread {
  background: #BC0C24; color: #fff;
  font-size: 10px; font-weight: 800;
  padding: 1px 7px; border-radius: 999px;
  white-space: nowrap; flex-shrink: 0; line-height: 1.5;
}
.ach-ttc-email {
  display: block; font-size: 10.5px; color: rgba(255,255,255,0.55);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  margin-top: 2px;
}

.ach-ttc-divider { height: 1px; background: rgba(255,255,255,0.09); margin: 10px 0; }

.ach-ttc-body { display: flex; flex-direction: column; gap: 6px; }
.ach-ttc-status {
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; font-weight: 600; color: rgba(255,255,255,.75);
}
.ach-ttc-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }

.ach-ttc-atend {
  display: inline-flex; align-items: center; gap: 5px;
  align-self: flex-start;
  padding: 3px 9px; border-radius: 20px;
  font-size: 10px; font-weight: 700;
  background: rgba(14,165,233,.18); color: #38bdf8;
}
.ach-ttc-free {
  font-size: 10.5px; font-weight: 600; color: rgba(255,255,255,.4);
}
</style>
